import React, { createContext, useContext, useState, ReactNode } from 'react';
import Notification from '../components/Notification/Notification';

// Define the notification type
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

// Define the notification interface
interface NotificationItem {
  id: string;
  type: NotificationType;
  message: string;
}

// Define the context interface
interface NotificationContextType {
  showNotification: (type: NotificationType, message: string) => void;
  notifications: NotificationItem[];
  removeNotification: (id: string) => void;
}

// Create the context with default values
const NotificationContext = createContext<NotificationContextType>({
  showNotification: () => {},
  notifications: [],
  removeNotification: () => {}
});

// Hook to use the notification context
export const useNotification = () => useContext(NotificationContext);

// Provider component
interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Show a new notification
  const showNotification = (type: NotificationType, message: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message }]);
  };

  // Remove a notification by ID
  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ showNotification, notifications, removeNotification }}>
      {children}
      <div className="notification-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            type={notification.type}
            message={notification.message}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
